import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCircleIcon, TimesCircleIcon } from 'assets/icons';
import { useVerifyConfiguredDmsWorkshopId } from 'domains/garage/Garage.requests';
import { configureWorkshopId, getUserGarageWorkshopId } from 'domains/garage/Garage.store';
import { getDMSConfig } from 'domains/user';
import DmsCard from 'pages/SettingsPage/Dms/DmsCard';
import { PrerequisitiesWrapper } from 'pages/SettingsPage/Dms/DmsSettings.styled';
import { SectionWrapper } from 'pages/SettingsPage/General/GeneralSettings.styled';
import { theme } from 'styles';
import { BlackButton, CleverInput, Flex, Icon, Text } from 'UI';
import { isError } from 'utils';

const DmsSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const configuredDmsWorkshopId = useSelector(getUserGarageWorkshopId);
  const dmsConfig = useSelector(getDMSConfig);
  const dmsDataStatus = dmsConfig?.complementaryDmsData?.searchStatus;
  const [dmsWorkshopId, setDmsWorkshopId] = useState(configuredDmsWorkshopId);

  useEffect(() => {
    setDmsWorkshopId(configuredDmsWorkshopId);
  }, [configuredDmsWorkshopId]);

  useVerifyConfiguredDmsWorkshopId();

  const prerequisities = [
    t('settings.configure_dms.prerequisities.fiducal_mobility', 'Fiducial V-Mobility'),
    t('settings.configure_dms.prerequisities.software_winmotor', 'Software Winmotor'),
    t('settings.configure_dms.prerequisities.ebp_meca', 'EBP Meca'),
    t(
      'settings.configure_dms.prerequisities.identification_number_provided_by_publisher',
      'Identification number provided by your publisher.',
    ),
  ];

  const getInfoText = () => {
    if (isError(dmsDataStatus)) {
      return t('settings.configure_dms.identification_number_given', 'Something went wrong, please verify your input.');
    }
    return t('settings.configure_dms.identification_number_given', 'Identification number given by your editor.');
  };

  return (
    <Flex justify={'space-between'}>
      <Flex direction={'column'} gap={20} maxWidth={500}>
        <SectionWrapper>
          <Flex direction={'column'} gap={15}>
            <Text type={'h4_paragraph'}>{t('settings.configure_dms.setup_your_dms_id', 'Setup your DMS ID')}</Text>
            <Text type="base">
              {t('settings.configure_dms.linked_to', 'You are currently linked to {{dmsWorkshopId}}', {
                dmsWorkshopId: dmsWorkshopId ?? '...',
              })}
            </Text>

            <Flex gap={5}>
              <Text type={'h4_paragraph'}>{t('common.id', 'ID')}</Text>
              <Text type={'h4_paragraph'} displayStyle={'error'}>
                *
              </Text>
            </Flex>

            <Flex gap={30} maxWidth={400}>
              <CleverInput
                status={isError(dmsDataStatus) ? 'error' : undefined}
                suffix={
                  isError(dmsDataStatus) ? (
                    <Icon IconComponent={TimesCircleIcon} size={16} color={theme.color.error} />
                  ) : undefined
                }
                size={'middle'}
                bordered
                onChange={(val) => setDmsWorkshopId(val)}
                value={dmsWorkshopId}
              />
              <BlackButton
                disabled={!dmsWorkshopId}
                onClick={() =>
                  dmsWorkshopId &&
                  dmsWorkshopId !== configuredDmsWorkshopId &&
                  dispatch(configureWorkshopId(dmsWorkshopId))
                }
              >
                {t('profile.settings.configure_dms.save_button_label', 'Validate')}
              </BlackButton>
            </Flex>
            <Flex align={'center'} gap={5}>
              <Icon
                IconComponent={InfoCircleIcon}
                size={16}
                color={isError(dmsDataStatus) ? theme.color.error : theme.color.grey55}
              />
              <Text type="base" color={isError(dmsDataStatus) ? 'red' : 'grey55'}>
                {getInfoText()}
              </Text>
            </Flex>
          </Flex>
        </SectionWrapper>
        <Flex direction={'column'} gap={10}>
          <Text type={'h4_paragraph'} disableGutter>
            {t('settings.configure_dms.prerequisities.title', 'Prerequisities')}
          </Text>
          <Text type="base">{t('settings.configure_dms.prerequisities.description', 'Must be compatible with:')}</Text>
          <PrerequisitiesWrapper>
            {prerequisities.map((prerequisity) => (
              <li key={prerequisity}>
                <Text type="base">{prerequisity}</Text>
              </li>
            ))}
          </PrerequisitiesWrapper>
        </Flex>
      </Flex>
      <DmsCard />
    </Flex>
  );
};

export default DmsSettings;
